import { Button } from '@/components/ui-kit/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface PaginationState {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface TablePaginationProps {
  pagination: PaginationState;
  onPageChange: (page: number) => void;
  showingLabel?: string;
}

/**
 * Generates an array of page numbers to display with ellipsis
 * Shows: first page, last page, current page, and 1 page on each side of current
 */
function getPageNumbers(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];
  const showEllipsisStart = currentPage > 3;
  const showEllipsisEnd = currentPage < totalPages - 2;

  // Always show first page
  pages.push(1);

  if (showEllipsisStart) {
    pages.push('ellipsis');
  }

  // Pages around current
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i++) {
    if (!pages.includes(i)) {
      pages.push(i);
    }
  }

  if (showEllipsisEnd) {
    pages.push('ellipsis');
  }

  // Always show last page
  if (!pages.includes(totalPages)) {
    pages.push(totalPages);
  }

  return pages;
}

export function TablePagination({ pagination, onPageChange, showingLabel }: TablePaginationProps) {
  const { t } = useTranslation();

  const startItem = (pagination.page - 1) * pagination.limit + 1;
  const endItem = Math.min(pagination.page * pagination.limit, pagination.total);
  const pageNumbers = getPageNumbers(pagination.page, pagination.totalPages);

  return (
    <div className="flex items-center justify-between px-2 py-2">
      <div className="text-sm text-muted-foreground">
        {showingLabel ??
          t('TABLE_PAGINATION_SHOWING_RANGE', {
            start: startItem,
            end: endItem,
            total: pagination.total,
            defaultValue: `Showing {{start}}-{{end}} of {{total}}`,
          })}
      </div>
      <div className="inline-flex items-center rounded-md border border-input">
        {/* Previous button */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-none rounded-l-md border-r border-input"
          onClick={() => onPageChange(pagination.page - 1)}
          disabled={pagination.page <= 1}
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="sr-only">{t('PREVIOUS')}</span>
        </Button>

        {/* Page numbers */}
        {pageNumbers.map((pageNum, index) =>
          pageNum === 'ellipsis' ? (
            <span
              key={`ellipsis-${index}`}
              className="flex h-8 w-8 items-center justify-center border-r border-input text-sm text-muted-foreground"
            >
              ...
            </span>
          ) : (
            <Button
              key={pageNum}
              variant={pagination.page === pageNum ? 'default' : 'ghost'}
              size="icon"
              className={`h-8 w-8 rounded-none border-r border-input ${
                pagination.page === pageNum ? 'bg-primary/10 text-primary hover:bg-primary/20' : ''
              }`}
              onClick={() => onPageChange(pageNum)}
            >
              {pageNum}
            </Button>
          )
        )}

        {/* Next button */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-none rounded-r-md"
          onClick={() => onPageChange(pagination.page + 1)}
          disabled={pagination.page >= pagination.totalPages}
        >
          <ChevronRight className="h-4 w-4" />
          <span className="sr-only">{t('NEXT')}</span>
        </Button>
      </div>
    </div>
  );
}
